import React, { useState } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Box,
  Button,
} from "@mui/material";
import {
  MapPin,
  BedDouble,
  Bath,
  Maximize,
  Star,
  Eye,
  Building,
} from "lucide-react";

const PropertyCard = ({ property }) => {
  const lang = localStorage.getItem("language") || "arabic";
  const [hovered, setHovered] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const toggleFav = (e) => {
    e.stopPropagation();
    setFavorite((prev) => !prev);
  };
  const p = property[lang];

  return (
    <motion.div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      whileHover={{ y: -4 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Link
        to={{
          pathname: `/real-estate/view/${property._id}`,
          state: { property },
        }}
      >
        <Card
          sx={{
            position: "relative",
            borderRadius: 2,
            boxShadow: 2,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            fontFamily: '"Alexandria", sans-serif',
          }}
        >
          <Box sx={{ position: "relative", pt: "56.25%" }}>
            <CardMedia
              component="img"
              image={property.image}
              alt={p.title}
              loading="lazy"
              decoding="async"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                display: "flex",
                gap: 0.5,
                zIndex: 3,
              }}
            >
              <Chip
                label={p.type}
                size="small"
                color="primary"
                sx={{
                  fontFamily: '"Alexandria", sans-serif',
                  lineHeight: 2,
                  fontSize: 10,
                }}
              />
              <Chip
                label={p.availability}
                size="small"
                color={
                  p.availability === "Rent" || p.availability === "إيجار"
                    ? "success"
                    : "secondary"
                }
                sx={{
                  fontFamily: '"Alexandria", sans-serif',
                  lineHeight: 2,
                  fontSize: 10,
                }}
              />
            </Box>
            {property.featured && (
              <Box
                onClick={toggleFav}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 3,
                  bgcolor: "#FFD700",
                  borderRadius: 1,
                  px: 0.8,
                  py: 0.4,
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#FFC200" },
                }}
              >
                <Star size={16} color="#fff" />
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    ml: 0.5,
                    color: "#fff",
                    fontWeight: 700,
                    fontFamily: '"Alexandria", sans-serif',
                    fontSize: 10,
                  }}
                >
                  {lang === "arabic" ? "مميز" : "Featured"}
                </Typography>
              </Box>
            )}
            <AnimatePresence>
              {hovered && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(0,0,0,0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    endIcon={<Eye size={14} />}
                    sx={{
                      p: 1.5,
                      fontSize: 11,
                      fontFamily: '"Alexandria", sans-serif',
                      textTransform: "none",
                      flexDirection: "row-reverse",
                      "& .MuiButton-endIcon": { marginLeft: 1, marginRight: 1 },
                    }}
                  >
                    {lang === "arabic" ? "التفاصيل" : "Details"}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>

          <CardContent sx={{ flexGrow: 1, py: 2, px: 2 }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{ fontFamily: '"Alexandria", sans-serif', fontWeight: 600 }}
            >
              {p.title}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
              <MapPin size={16} color="rgb(59 130 246)" />
              <Typography
                variant="caption"
                sx={{ fontFamily: '"Alexandria", sans-serif', ml: 0.5 }}
                noWrap
              >
                {p.location}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 1.5,
                fontSize: 12,
                color: "text.secondary",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                <BedDouble size={18} color="rgb(59 130 246)" /> {property.beds}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                <Bath size={18} color="rgb(59 130 246)" /> {property.baths}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                <Maximize size={18} color="rgb(59 130 246)" /> {property.sqft}
              </Box>
            </Box>
          </CardContent>

          <CardActions sx={{ px: 2, py: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexGrow: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                color="primary"
                className="icon-saudi_riyal"
                sx={{ fontFamily: '"Alexandria", sans-serif', fontWeight: 700 }}
              >
                {p.price}
              </Typography>
            </Box>
            <Chip
              icon={<Building size={13} color="rgb(59 130 246)" />}
              label={p.availability}
              size="small"
              sx={{
                bgcolor: "rgba(25, 118, 210, 0.08)",
                color: "rgb(25 118 210)",
                borderRadius: "10px",
                fontFamily: '"Alexandria", sans-serif',
                fontSize: 10,
                lineHeight: 2,
                p: 2,
              }}
            />
          </CardActions>
        </Card>
      </Link>
    </motion.div>
  );
};

PropertyCard.propTypes = {
  property: PropTypes.object.isRequired,
};

export default PropertyCard;
